import React from 'react'
import PropTypes from 'prop-types'
import Label from './Label'
import Select from './Select'
import Control from './Control'
import Field from './Field'

/* doc
---
title: SelectField
name: SelectField
category: React - Bulma
parent: Field
---

A `SelectField` is a wrapper for combining `Field` and `Select` components.

Use this if you need to add a Select field to a form.

See [Bulma Documentation](http://bulma.io/documentation/form/select/) for reference and full options.
If one of the Bulma options isn't listed below, it may be added using the `className` prop.

### Props
| Prop Name       | Description                                   |Type            | Default Value             | Required?     |
|-----------------|-----------------------------------------------|----------------|---------------------------|---------------|
| name            | Name/id for select field                      | string         |                           | yes           |
| value           | defaultValue for input                        | any            |                           | no            |
| options         | array of option values to render              | array          |                           | no            |
| size            | Size of input                                 | string         |                           | no            |
| label           | Label's display text                          | string         |                           | no            |
| fieldProps      | Props for `Field` component                   | object         | {}                        | no            |
| labelProps      | Props for `Label` component                   | object         | {}                        | no            |
| controlProps    | Props for `Control` component                 | object         | {}                        | no            |
| inputProps      | Props for `Input` component                   | object         | {}                        | no            |


```html_example
  <SelectField
    name="example"
    value="starting value"
    label="Example Input Field"
  />
```
*/

/**
* SelectField Component. Used to maintain consistency for html/css regarding
* input fields.
*
* @TODO Add support for input colors/states.
* @TODO Add support for icons.
* @TODO Add support for help text.
*/
function SelectField(props) {
  const {
    onChange, label, children, name, options, size, fieldProps, labelProps, controlProps, inputProps, ...rest
  } = props

  // Helper function to render options from the `options` prop.
  const renderOptions = () => options
    .map((option) => {
      const isString = typeof option === 'string'
      const optionValue = isString ? option : option.value
      const optionLabel = isString ? option : option.label

      return (<option key={optionValue} value={optionValue}>{optionLabel}</option>)
    })

  return (
    <Field {...fieldProps}>
      {label ? <Label htmlFor={name} size={size} {...labelProps}>{label}</Label> : false}
      <Control {...controlProps}>
        <Select
          inputSize={size}
          {...inputProps}
          {...rest}
          onChange={onChange}
          name={name}
        >
          {renderOptions()}
          {children}
        </Select>
      </Control>
    </Field>
  )
}

SelectField.defaultProps = {
  options: [],
  fieldProps: {},
  labelProps: {},
  controlProps: {},
  inputProps: {},
}

SelectField.propTypes = {
  // Input name/id
  name: PropTypes.string.isRequired,
  // Default value for input.
  value: PropTypes.any,
  // List of select options to render. Array of strings or objects with `value` and `label` keys.
  options: PropTypes.array,
  // Label display text.
  label: PropTypes.string,
  // Props for `Field` component.
  fieldProps: PropTypes.object,
  // Props for `Label` component.
  labelProps: PropTypes.object,
  // Props for `Control` component.
  controlProps: PropTypes.object,
  // Props for `Input` component.
  inputProps: PropTypes.object,
}

export default SelectField
